import { useMemo } from 'react';
import { Skeleton } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { DefaultPageLayout } from 'layouts/default-page-layout';
import { useGetTransportPerformance } from 'api/statistic/use-get-transport-performance';
import type { TTachoInfoRes } from 'api/swagger';
import { useGetTachoInfo } from 'api/transports/use-get-tacho-info';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard';
import { useTransportMileageFiltersStore } from 'shared/stores/use-transport-mileage-filters';
import { useTransportPerformanceGroupsStore } from 'shared/stores/use-transport-performance-groups';
import { TransportMileageFilters } from 'components/transport-mileage/transport-mileage-filters';
import { TransportMileageList } from 'components/transport-mileage/transport-mileage-list';
import { TransportMileageSummary } from 'components/transport-mileage/transport-mileage-summary';
import { TransportMileageTabs } from 'components/transport-mileage/transport-mileage-tabs';
import styles from './transport-mileage.page.module.scss';
import { filterSeriesByGroup } from './utils';

export const TransportMileagePageComponent: React.FC = () => {
  const { filters, search } = useTransportMileageFiltersStore();
  const { data, isLoading, isPreviousData } = useGetTransportPerformance({ filters, search });
  const { data: tachoInfo } = useGetTachoInfo({ isEnabled: true });

  const { groups, activeTab, selectedIds, setSelectedIds } = useTransportPerformanceGroupsStore();

  const tachoDataMap = useMemo(() => {
    const map = new Map<number, TTachoInfoRes[]>();

    if (!tachoInfo) return map;

    tachoInfo.forEach((item) => {
      if (map.has(item.transportId)) {
        map.get(item.transportId)?.push(item);
      } else {
        map.set(item.transportId, [item]);
      }
    });

    return map;
  }, [tachoInfo]);

  const visibleSeries = filterSeriesByGroup(data?.series || [], activeTab, groups);

  const header = <PageHeader title={<TransportMileageFilters key='filters' />} />;

  return (
    <DefaultPageLayout header={header}>
      <div className={styles.container}>
        <Skeleton loading={isLoading}>
          {data?.total && <TransportMileageSummary summary={data.total} />}
          <TransportMileageTabs />
          <TransportMileageList
            transportMileages={visibleSeries}
            isLoading={isPreviousData}
            tachoDataMap={tachoDataMap}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        </Skeleton>
      </div>
    </DefaultPageLayout>
  );
};

export const TransportMileagePage = withRoleGuard(TransportMileagePageComponent, [UserRole.admin, UserRole.assistant]);
